import React from 'react';
import { AnimatePresence, motion } from 'motion/react';

// Caps how many status lines stack up during a long turn (repeated tool
// calls, several searches) — only the tail is ever useful to a user
// watching live.
const MAX_VISIBLE_LINES = 4;

// COL-39: honest copy only. A phase this doesn't recognize, or a tool_end
// with no real count to report, returns null and toStatusLines drops it —
// never guess or pad with a fake number.
function describeToolStart({ tool, query, category, playlist_name: playlistName }) {
  switch (tool) {
    case 'search_collection':
      return query
        ? `Searching your collection for "${query}"…`
        : 'Searching your collection…';
    case 'list_items':
      return category ? `Browsing your ${category} items…` : 'Browsing your items…';
    case 'get_item_details':
      return 'Looking up that item…';
    case 'search_web':
      return query ? `Searching the web for "${query}"…` : 'Searching the web…';
    case 'search_places':
      return query ? `Looking up places for "${query}"…` : 'Looking up places…';
    case 'add_to_playlist':
      return playlistName
        ? `Adding to "${playlistName}"…`
        : 'Adding to your playlist…';
    default:
      return null;
  }
}

// COL-266: emitted while a tool call is backing off and retrying (e.g. an
// Apple Music 5xx/429) -- keeps the turn from looking frozen during the
// retry sleep. Only add_to_playlist emits this today; other tools fall
// through to the generic copy below rather than showing nothing.
function describeToolRetry({ tool }) {
  switch (tool) {
    case 'add_to_playlist':
      return 'Apple Music is slow — retrying…';
    default:
      return 'Retrying…';
  }
}

function describeToolEnd({ tool, count }) {
  // No count means nothing honest to say — get_item_details/search_web/
  // search_places don't produce an item count, and a fake "Found 0" would
  // be worse than just not showing a line for it.
  if (typeof count !== 'number') return null;
  if (count === 0) {
    return tool === 'list_items' ? 'Nothing there yet' : 'No matches found';
  }
  return `Found ${count} item${count === 1 ? '' : 's'}`;
}

export function describeEvent(event) {
  switch (event.phase) {
    case 'thinking':
      return 'Thinking';
    case 'tool_start':
      return describeToolStart(event);
    case 'tool_end':
      return describeToolEnd(event);
    case 'tool_retry':
      return describeToolRetry(event);
    case 'composing':
      return 'Reading results and writing';
    default:
      // Unrecognized phase (future backend addition an older frontend
      // hasn't shipped copy for yet) — skip rather than guess.
      return null;
  }
}

export function toStatusLines(events) {
  const lines = [];
  for (const event of events || []) {
    const line = describeEvent(event);
    if (!line) continue;
    // Collapse an immediate repeat (e.g. two tool_start events that map to
    // the same generic copy) rather than showing the same line twice in a row.
    if (lines[lines.length - 1] === line) continue;
    lines.push(line);
  }
  return lines.slice(-MAX_VISIBLE_LINES);
}

/**
 * Staged status text for an in-flight chat turn (COL-39), rendered in place
 * of the bare typing indicator. Falls back to the exact same bouncing-dots
 * markup pre-COL-39 code used whenever there's nothing honest to say yet
 * (turn just started, or every event so far mapped to null) — a caller
 * with no progress_token wiring, or on a cold poll, sees an identical
 * loading state to before this feature existed.
 */
export default function ChatProgress({ events }) {
  const lines = toStatusLines(events);

  if (lines.length === 0) {
    return (
      <div className="chat-typing-indicator">
        <span className="chat-bounce-dot" />
        <span className="chat-bounce-dot" />
        <span className="chat-bounce-dot" />
      </div>
    );
  }

  return (
    <div className="chat-progress">
      <AnimatePresence initial={false}>
        {lines.map((line, index) => {
          const isCurrent = index === lines.length - 1;
          return (
            <motion.div
              key={`${index}-${line}`}
              className={
                isCurrent ? 'chat-progress-line chat-progress-line-current' : 'chat-progress-line'
              }
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <span>{line}</span>
              {isCurrent && (
                <span className="chat-progress-dots">
                  <span className="chat-bounce-dot" />
                  <span className="chat-bounce-dot" />
                  <span className="chat-bounce-dot" />
                </span>
              )}
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}
